/**
 * Re-number widget `Order` fields so every parent's children get sequential,
 * tie-free values (0, 1, 2…) that match the order the viewer currently renders.
 *
 * Why: the viewer sorts widgets by `Order` and lets Firestore break ties by
 * document ID. Widgets saved without an explicit order all share `Order: 0`,
 * so their on-screen order silently depends on their docids. Copying such
 * widgets to new docs (report duplication) re-rolls the tiebreak and shuffles
 * the layout. Normalizing before the copy pins the rendered order into the
 * data itself.
 *
 * @param {Array<{ID: string, Parent: string|null, Order?: number, docid: string}>} widgets
 *   Serialized widget docs, each carrying its source Firestore docid.
 * @returns {Array<object>} New array of new objects, same fields, `Order` re-numbered per parent.
 */
const normalizeSiblingOrder = (widgets) => {
    const sorted = [...widgets].sort((a, b) => {
        const orderDiff = (Number(a.Order) || 0) - (Number(b.Order) || 0);
        if (orderDiff !== 0) return orderDiff;
        // Firestore breaks orderBy ties by document name (lexicographic).
        return a.docid < b.docid ? -1 : (a.docid > b.docid ? 1 : 0);
    });

    /** @type {Map<string|null, number>} next Order value per parent ID */
    const nextOrderByParent = new Map();

    return sorted.map((widget) => {
        const parentKey = widget.Parent ?? null;
        const order = nextOrderByParent.get(parentKey) ?? 0;
        nextOrderByParent.set(parentKey, order + 1);
        return { ...widget, Order: order };
    });
};

export { normalizeSiblingOrder };
